import axios from 'axios';
import { toast } from 'react-hot-toast';
import { API_BASE_URL } from '../config/env';

// API Base URL - configured via environment (default: production)

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Logout callback - will be set by AuthContext
let logoutCallback = null;

// Function to set logout callback from AuthContext
export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    // Only logout on explicit authentication errors, not all errors
    // This prevents false positives from regular API failures
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors ONLY
    if (error.response && error.response.status === 401) {
      // Check if this is a login attempt (don't logout during login)
      if (!error.config?.url?.includes('/api/login')) {
        console.warn('🔐 Session expired - logging out');
        handleLogout();
      }
    }
    return Promise.reject(error);
  }
);

// Handle logout and redirect
const handleLogout = () => {
  // Show notification
  toast.error('Session expired. Please login again.', {
    duration: 4000,
  });
  
  // Clear localStorage
  localStorage.removeItem('rodbot_token');
  localStorage.removeItem('rodbot_store_id');
  localStorage.removeItem('rodbot_user');
  
  // Call logout callback if set
  if (logoutCallback) {
    logoutCallback();
  }
  
  // Redirect to login page after a short delay
  // NOTE: App uses HashRouter, so routes are /#/login, NOT /login
  setTimeout(() => {
    if (!window.location.hash.includes('/login')) {
      window.location.hash = '#/login';
    }
  }, 500);
};

// Get auth data from localStorage
const getAuthData = () => {
  const token = localStorage.getItem('rodbot_token');
  const storeId = localStorage.getItem('rodbot_store_id');
  return { token, storeId };
};

const getAuthHeaders = () => {
  const { token } = getAuthData();
  if (!token) throw new Error('Not authenticated');
  return { Authorization: `Bearer ${token}` };
};

// Generic API request for control panel endpoints
const controlRequest = async (endpoint, action, data = {}) => {
  const { token, storeId } = getAuthData();
  
  if (!token || !storeId) {
    throw new Error('Not authenticated');
  }
  
  const payload = {
    token,
    store_id: storeId,
    action,
    ...data,
  };
  
  try {
    const response = await apiClient.post(`/api/control/${endpoint}`, payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.error || 'Request failed');
    }
    throw error;
  }
};

// ============ Workers API ============
export const workersApi = {
  list: () => controlRequest('workers', 'list'),
  
  get: (workerId) => controlRequest('workers', 'get', { worker_id: workerId }),
  
  create: (name) => controlRequest('workers', 'create', { name }),
  
  update: (workerId, data) => controlRequest('workers', 'update', {
    worker_id: workerId,
    ...data,
  }),
  
  delete: (workerId) => controlRequest('workers', 'delete', { worker_id: workerId }),
};

// ============ Schedules API ============
export const schedulesApi = {
  list: (workerId = null) => controlRequest('schedules', 'list', 
    workerId ? { worker_id: workerId } : {}
  ),
  
  get: (scheduleId) => controlRequest('schedules', 'get', { schedule_id: scheduleId }),
  
  create: (data) => controlRequest('schedules', 'create', data),
  
  update: (scheduleId, data) => controlRequest('schedules', 'update', {
    schedule_id: scheduleId,
    ...data,
  }),
  
  delete: (scheduleId) => controlRequest('schedules', 'delete', { schedule_id: scheduleId }),
};

// ============ Appointment Types API ============
export const appointmentTypesApi = {
  list: () => controlRequest('appointment-types', 'list'),
  
  get: (typeId) => controlRequest('appointment-types', 'get', { type_id: typeId }),
  
  create: (data) => controlRequest('appointment-types', 'create', data),
  
  update: (typeId, data) => controlRequest('appointment-types', 'update', {
    type_id: typeId,
    ...data,
  }),
  
  delete: (typeId) => controlRequest('appointment-types', 'delete', { type_id: typeId }),
};

// ============ Appointments API ============
export const appointmentsApi = {
  list: (filters = {}) => controlRequest('appointments', 'list', filters),
  
  get: (appointmentId) => controlRequest('appointments', 'get', { appointment_id: appointmentId }),
  
  create: (data) => controlRequest('appointments', 'create', data),
  
  update: (appointmentId, data) => controlRequest('appointments', 'update', {
    appointment_id: appointmentId,
    ...data,
  }),
  
  delete: (appointmentId, sendNotification = true) => controlRequest('appointments', 'delete', { 
    appointment_id: appointmentId,
    send_notification: sendNotification,
  }),
  
  getAvailableSlots: (date, appointmentTypeId, workerId = null) => 
    controlRequest('appointments', 'available_slots', {
      date,
      appointment_type_id: appointmentTypeId,
      ...(workerId ? { worker_id: workerId } : {}),
    }),
};

// ============ Public Booking Link ============
export const bookingLinkApi = {
  get: () => controlRequest('booking-link', 'get'),
  update: (data) => controlRequest('booking-link', 'update', data),
  upload: async (field, file) => {
    const { token, storeId } = getAuthData();
    if (!token || !storeId) throw new Error('Not authenticated');
    const form = new FormData();
    form.append('token', token);
    form.append('store_id', storeId);
    form.append('field', field);
    form.append('file', file);
    const response = await apiClient.post('/api/control/booking-link/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
};

// ============ Store Info API ============
export const storeApi = {
  getInfo: () => controlRequest('store-info', null),
  updatePhone: (phoneNumber) => controlRequest('update-phone', null, { phone_number: phoneNumber }),
};

// ============ Store Prompts API ============
export const promptsApi = {
  get: () => controlRequest('store-prompts', 'get'),
  
  update: (data) => controlRequest('store-prompts', 'update', data),
};

// ============ Bot Config API ============
export const botConfigApi = {
  get: () => controlRequest('bot-config', 'get'),
  
  update: (data) => controlRequest('bot-config', 'update', data),
};

// ============ Messages API ============
export const messagesApi = {
  // List all chats
  listChats: (limit = 100, page = 1) => controlRequest('messages', 'list_chats', {
    limit,
    page,
  }),
  
  // List messages for the logged-in user's phone number
  list: (phoneNumber = null, limit = 100) => controlRequest('messages', 'list', {
    ...(phoneNumber ? { phone_number: phoneNumber } : {}),
    limit,
  }),
  
  // List messages for a specific chat ID
  listByChat: (chatId, limit = 100, page = 1) => controlRequest('messages', 'list_by_chat', {
    chat_id: chatId,
    limit,
    page,
  }),
  
  // Send a message to a phone number (must be within 24-hour window)
  send: (phoneNumber, message) => controlRequest('send-message', null, {
    phone_number: phoneNumber,
    message,
  }),

  contactAction: (phoneNumber, action) => controlRequest('chat-actions', action, {
    phone_number: phoneNumber,
  }),

  // Authenticated URL for streaming a WhatsApp media attachment (for <img>/<video>).
  mediaUrl: (mediaId) => {
    const { token, storeId } = getAuthData();
    if (!mediaId || !token || !storeId) return '';
    const params = new URLSearchParams({ token, store_id: storeId });
    return `${API_BASE_URL}/api/control/media/${encodeURIComponent(mediaId)}?${params.toString()}`;
  },
};

// ============ Leads Analytics API ============
export const leadsApi = {
  // Get dashboard data (leads, message stats, appointment stats)
  getDashboard: (period = 'year') => controlRequest('leads-analytics', 'dashboard', { period }),
  
  // Get contacts/leads list
  getContacts: (period = 'year') => controlRequest('leads-analytics', 'contacts', { period }),
  
  // Get message statistics
  getMessageStats: (period = 'year') => controlRequest('leads-analytics', 'message_stats', { period }),
  
  // Get appointment statistics
  getAppointmentStats: (period = 'year') => controlRequest('leads-analytics', 'appointment_stats', { period }),

  // ─── Lead Management ───
  // List all leads for this store
  listLeads: (status = null) => controlRequest('leads', 'list', status ? { status } : {}),

  // Create (or fetch) a single lead from a WhatsApp conversation
  createFromChat: ({ phone, name, chatId }) =>
    controlRequest('leads', 'create_from_chat', { phone, name, chat_id: chatId }),

  // Update lead status (new / contacted / converted / blocked)
  updateLeadStatus: (leadId, status) => controlRequest('leads', 'update_status', { lead_id: leadId, status }),

  // Block a lead (adds to phone controls)
  blockLead: (leadId) => controlRequest('leads', 'block', { lead_id: leadId }),

  // Delete a lead
  deleteLead: (leadId) => controlRequest('leads', 'delete', { lead_id: leadId }),

  // Fix dates: align created_at with actual Chakra chat timestamps
  fixDates: () => controlRequest('leads', 'fix_dates', {}),

  // ─── Custom Lead Statuses ───
  // List the store's status definitions (built-in + custom)
  listStatuses: () => controlRequest('leads', 'list_statuses', {}),

  // Create a custom status (label + color)
  createStatus: (label, color) => controlRequest('leads', 'create_status', { label, color }),

  // Rename / recolour a status
  updateStatusDef: (statusId, data) => controlRequest('leads', 'update_status_def', { status_id: statusId, ...data }),

  // Delete a custom status (its leads move back to 'new')
  deleteStatus: (statusId) => controlRequest('leads', 'delete_status', { status_id: statusId }),
};

// ============ Profile Picture Upload API ============
export const profileApi = {
  uploadPicture: async (file) => {
    const { token, storeId } = getAuthData();
    if (!token || !storeId) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('token', token);
    formData.append('store_id', storeId);

    const response = await apiClient.post('/api/control/upload-profile-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
};

// ============ Store Pictures API ============
export const picturesApi = {
  list: () => controlRequest('store-pictures', 'list'),

  rename: (pictureId, name) => controlRequest('store-pictures', 'rename', {
    picture_id: pictureId,
    name,
  }),

  delete: (pictureId) => controlRequest('store-pictures', 'delete', { picture_id: pictureId }),

  upload: async (file, name) => {
    const { token, storeId } = getAuthData();
    if (!token || !storeId) throw new Error('Not authenticated');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('token', token);
    formData.append('store_id', storeId);

    const response = await apiClient.post('/api/control/upload-store-picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
    return response.data;
  },
};

// ============ Store Interactive Messages API ============
export const interactivesApi = {
  list: () => controlRequest('store-interactives', 'list'),

  create: ({ name, kind, body_text, button_text, items }) =>
    controlRequest('store-interactives', 'create', { name, kind, body_text, button_text, items }),

  update: (interactiveId, data) =>
    controlRequest('store-interactives', 'update', { interactive_id: interactiveId, ...data }),

  delete: (interactiveId) =>
    controlRequest('store-interactives', 'delete', { interactive_id: interactiveId }),
};

// ============ Bot Builder ("Build it for me" / "Tweak it") API ============
export const botBuilderApi = {
  generate: (description, language) =>
    controlRequest('generate-bot-config', null, { description, language }),

  // Edit the existing config from a request, optionally with a chat transcript.
  tweak: (instruction, current, language, transcript = '') =>
    controlRequest('tweak-bot-config', null, { instruction, current, language, transcript }),
};

// ============ Bot Playground (live test chat) API ============
export const botPlaygroundApi = {
  // messages: [{role:'user'|'assistant', content}], overrides: unsaved form state
  send: (text, messages = [], overrides = {}) =>
    controlRequest('bot-playground', null, { text, messages, overrides }),
};

// ============ Products API ============
export const productsApi = {
  list: (page = 1, limit = 50, categoryId = null) =>
    controlRequest('products', 'list', {
      page,
      limit,
      ...(categoryId !== null ? { category_id: categoryId } : {}),
    }),
  
  get: (productId) => controlRequest('products', 'get', { product_id: productId }),
  
  create: (data) => controlRequest('products', 'create', data),
  
  update: (productId, data) => controlRequest('products', 'update', {
    product_id: productId,
    ...data,
  }),
  
  delete: (productId) => controlRequest('products', 'delete', { product_id: productId }),
};

// ============ Product Categories (Control Panel) ============
export const categoriesApi = {
  list: () => controlRequest('product-categories', 'list'),
  
  get: (categoryId) => controlRequest('product-categories', 'get', { category_id: categoryId }),
  
  create: (data) => controlRequest('product-categories', 'create', data),
  
  update: (categoryId, data) => controlRequest('product-categories', 'update', {
    category_id: categoryId,
    ...data,
  }),
  
  delete: (categoryId) => controlRequest('product-categories', 'delete', { category_id: categoryId }),
};

// ============ Phone Controls (Control Panel) ============
export const phoneControlsApi = {
  list: async () => {
    const res = await apiClient.get('/api/control-panel/phone-controls', {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  stats: async () => {
    const res = await apiClient.get('/api/control-panel/stats', {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  recentContacts: async () => {
    const res = await apiClient.get('/api/control-panel/recent-contacts', {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  upsert: async (payload) => {
    const res = await apiClient.post('/api/control-panel/phone-controls', payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  delete: async (id) => {
    const res = await apiClient.delete(`/api/control-panel/phone-controls/${id}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
};

// ============ WhatsApp Connect (Embedded Signup) ============
export const whatsappApi = {
  // Generate a Chakra embedded-signup connect token for the logged-in client
  getConnectToken: async () => {
    const res = await apiClient.post('/api/whatsapp/connect-token', {}, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  // Persist the WABA / phone / plugin returned by the SDK onSuccess callback
  saveConnection: async (data) => {
    const res = await apiClient.post('/api/whatsapp/connect-success', data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
  // Current connection status for the logged-in client
  getStatus: async () => {
    const res = await apiClient.get('/api/whatsapp/status', {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
};

const apiExports = {
  workers: workersApi,
  schedules: schedulesApi,
  appointmentTypes: appointmentTypesApi,
  appointments: appointmentsApi,
  bookingLink: bookingLinkApi,
  store: storeApi,
  messages: messagesApi,
  prompts: promptsApi,
  botConfig: botConfigApi,
  leads: leadsApi,
  products: productsApi,
  pictures: picturesApi,
  botBuilder: botBuilderApi,
  botPlayground: botPlaygroundApi,
  phoneControls: phoneControlsApi,
  profile: profileApi,
  whatsapp: whatsappApi,
};

export default apiExports;

